import { queryOptions } from '@tanstack/react-query'
import { apiGet, apiPost, apiPostForm, type PostValue } from './client'

export type OpteemumTab = {
  id: string
  label: string
  icon: string
  href: string
  group?: string
  groupIcon?: string
  right: string
  external?: boolean
}

export type OpteemumShell = {
  pageTitle: string
  moduleUrl: string
  tabs: OpteemumTab[]
}

export const TABS = [
  'hardware_alarms',
  'cmd_categories',
  'cmd_command_types',
  'cmd_commands',
  'standard_commands',
  'agent_commands',
  'macos_commands',
  'manage_applications',
  'softwares_tagging',
  'manage_kits',
  'manage_custom_kits',
  'manage_custom_packages',
  'startup_applications_manager',
] as const

export type OpteemumTabId = (typeof TABS)[number]

export function isOpteemumTab(value: string): value is OpteemumTabId {
  return (TABS as readonly string[]).includes(value)
}

export function opteemumShellQuery() {
  return queryOptions({
    queryKey: ['settings', 'opteemum', 'shell'],
    queryFn: () => apiGet<OpteemumShell>('/settings/opteemum'),
  })
}

export function applicationsQuery() {
  return queryOptions({
    queryKey: ['settings', 'opteemum', 'applications'],
    queryFn: () =>
      apiGet<{ app_categories: Array<Record<string, unknown>>; moduleUrl: string }>('/settings/opteemum/applications'),
  })
}

/**
 * O pagina din catalog. Categoria (coloana din stanga), cautarea si pagina
 * pleaca la server; browserul nu mai primeste tot catalogul.
 *
 * `appType`: -1 toate, 0 cele fara categorie, altfel categoria ceruta.
 */
export function applicationsTableQuery(r: { appType: number; start: number; length: number; search: string }) {
  const params = new URLSearchParams({
    appType: String(r.appType),
    start: String(r.start),
    length: String(r.length),
    search: r.search,
  })

  return queryOptions({
    queryKey: ['settings', 'opteemum', 'applications-table', r],
    queryFn: () => apiGet<OpteemumTable>('/settings/opteemum/applications/table?' + params.toString()),
  })
}

export function changeApplicationCategory(application_id: number, category: number) {
  return apiPost<{ ok?: boolean }>('/settings/opteemum/applications/category', { application_id, category })
}

export function newCategory(category_name: string) {
  return apiPost<{ ok?: boolean }>('/settings/opteemum/applications/categories', { category_name })
}

/**
 * O pagina de aplicatii cu eticheta ceruta. Eticheta (coloana din stanga),
 * cautarea si pagina pleaca la server.
 */
export function taggingTableQuery(r: { tag: number; start: number; length: number; search: string }) {
  const params = new URLSearchParams({
    tag: String(r.tag),
    start: String(r.start),
    length: String(r.length),
    search: r.search,
  })

  return queryOptions({
    queryKey: ['settings', 'opteemum', 'tagging-table', r],
    queryFn: () => apiGet<OpteemumTable>('/settings/opteemum/tagging/table?' + params.toString()),
  })
}

export function changeApplicationTag(application_id: number, tag: string) {
  return apiPost<{ ok?: boolean }>('/settings/opteemum/tagging', { application_id, tag })
}

export function kitsQuery() {
  return queryOptions({
    queryKey: ['settings', 'opteemum', 'kits'],
    queryFn: () =>
      apiGet<{
        softwares: Array<Record<string, unknown>>
        app_categories: Array<Record<string, unknown>>
        operating_systems: Array<Record<string, unknown>>
        packs: Array<Record<string, unknown>>
        moduleUrl: string
      }>('/settings/opteemum/kits'),
  })
}

/** O pagina din lista de kituri: cautarea si pagina pleaca la server. */
export function kitsTableQuery(r: { start: number; length: number; search: string }) {
  const params = new URLSearchParams({ start: String(r.start), length: String(r.length), search: r.search })

  return queryOptions({
    queryKey: ['settings', 'opteemum', 'kits-table', r],
    queryFn: () => apiGet<OpteemumTable>('/settings/opteemum/kits/table?' + params.toString()),
  })
}

export function removeKit(software: number) {
  return apiPost<{ ok?: boolean; message?: string }>('/settings/opteemum/kits/remove', { software })
}

export function customKitsQuery() {
  return queryOptions({
    queryKey: ['settings', 'opteemum', 'custom-kits'],
    queryFn: () =>
      apiGet<{
        companies: Array<Record<string, unknown>>
        categories: Array<Record<string, unknown>>
        operating_systems: Array<Record<string, unknown>>
        packages: Array<Record<string, unknown>>
        /** Firma omului: kitul se scrie NUMAI la ea (`addCustomKit`). */
        company_id: number
        moduleUrl: string
      }>('/settings/opteemum/custom-kits'),
  })
}

/** O pagina din lista de kituri proprii: cautarea si pagina pleaca la server. */
export function customKitsTableQuery(r: { start: number; length: number; search: string }) {
  const params = new URLSearchParams({ start: String(r.start), length: String(r.length), search: r.search })

  return queryOptions({
    queryKey: ['settings', 'opteemum', 'custom-kits-table', r],
    queryFn: () => apiGet<OpteemumTable>('/settings/opteemum/custom-kits/table?' + params.toString()),
  })
}

export function deleteCustomKit(ID: number) {
  return apiPost<{ ok?: boolean }>('/settings/opteemum/custom-kits/remove', { ID })
}

export function packagesQuery() {
  return queryOptions({
    queryKey: ['settings', 'opteemum', 'packages'],
    queryFn: () =>
      apiGet<{
        companies: Array<Record<string, unknown>>
        custom_kits: Array<Record<string, unknown>>
        main_kits: Array<Record<string, unknown>>
        /** Firma omului: pachetul se scrie NUMAI la ea (`addCustomPackage`). */
        company_id: number
        moduleUrl: string
      }>('/settings/opteemum/packages'),
  })
}

/** O pagina din lista de pachete: cautarea si pagina pleaca la server. */
export function packagesTableQuery(r: { start: number; length: number; search: string }) {
  const params = new URLSearchParams({ start: String(r.start), length: String(r.length), search: r.search })

  return queryOptions({
    queryKey: ['settings', 'opteemum', 'packages-table', r],
    queryFn: () => apiGet<OpteemumTable>('/settings/opteemum/packages/table?' + params.toString()),
  })
}

export function deleteCustomPackage(ID: number) {
  return apiPost<{ ok?: boolean }>('/settings/opteemum/packages/remove', { ID })
}

export function startupTableQuery() {
  return queryOptions({
    queryKey: ['settings', 'opteemum', 'startup-table'],
    queryFn: () => apiGet<{ data: Array<Record<string, unknown>> }>('/settings/opteemum/startup/table?start=0&length=200'),
  })
}

export function approveApplication(_application: number) {
  return apiPost<{ ok?: boolean }>('/settings/opteemum/startup/approve', { _application })
}

export function disapproveApplication(_application: number) {
  return apiPost<{ ok?: boolean }>('/settings/opteemum/startup/disapprove', { _application })
}

export function cmdCategoriesTableQuery() {
  return queryOptions({
    queryKey: ['settings', 'opteemum', 'cmd-categories'],
    queryFn: () => apiGet<{ data: Array<Record<string, unknown>> }>('/settings/opteemum/cmd/categories/table?start=0&length=200'),
  })
}

export function createCmdCategory(fields: Record<string, PostValue>) {
  return apiPost<{ ok?: boolean }>('/settings/opteemum/cmd/categories', fields)
}

/**
 * Setul din cod, adus in catalog. Se poate apasa de doua ori fara sa dubleze
 * nimic - fiecare rand importat isi tine cheia - deci raspunsul spune cate au
 * intrat si cate erau deja, in loc sa intrebe „esti sigur".
 */
export function importCmdBuiltins(osFamily: string) {
  return apiPost<{ success?: boolean; msg?: string }>(
    `/settings/opteemum/cmd/categories/import/${encodeURIComponent(osFamily)}`,
    {},
  )
}

export function removeCmdCategory(id: number) {
  return apiPost<{ ok?: boolean }>(`/settings/opteemum/cmd/categories/${id}/remove`, {})
}

export function editCmdCategory(id: number) {
  return apiGet<{ category: Record<string, unknown> }>(`/settings/opteemum/cmd/categories/${id}`)
}

export function updateCmdCategory(fields: Record<string, PostValue>) {
  return apiPost<{ ok?: boolean }>('/settings/opteemum/cmd/categories/update', fields)
}

export function cmdTypesTableQuery() {
  return queryOptions({
    queryKey: ['settings', 'opteemum', 'cmd-types'],
    queryFn: () => apiGet<{ data: Array<Record<string, unknown>> }>('/settings/opteemum/cmd/types/table?start=0&length=200'),
  })
}

export function editCmdCommandType(id: number) {
  return apiGet<{ type: Record<string, unknown> }>(`/settings/opteemum/cmd/types/${id}`)
}

/** `type_name` si `type` (id-ul), ca `editTypeForm` din macheta veche. */
export function updateCmdCommandType(fields: Record<string, PostValue>) {
  return apiPost<{ ok?: boolean }>('/settings/opteemum/cmd/types/update', fields)
}

export function removeCmdCommandType(id: number) {
  return apiPost<{ ok?: boolean }>(`/settings/opteemum/cmd/types/${id}/remove`, {})
}

export function createCmdCommandType(type_name: string) {
  return apiPost<{ ok?: boolean }>('/settings/opteemum/cmd/types', { type_name })
}

export function cmdCommandsQuery() {
  return queryOptions({
    queryKey: ['settings', 'opteemum', 'cmd-commands'],
    queryFn: () =>
      apiGet<{ categories: Array<Record<string, unknown>>; commandTypes: Array<Record<string, unknown>> }>(
        '/settings/opteemum/cmd/commands',
      ),
  })
}

export function cmdCommandsTableQuery() {
  return queryOptions({
    queryKey: ['settings', 'opteemum', 'cmd-commands-table'],
    queryFn: () => apiGet<{ data: Array<Record<string, unknown>> }>('/settings/opteemum/cmd/commands/table?start=0&length=200'),
  })
}

export function createCmdCommand(fields: Record<string, PostValue>) {
  return apiPost<{ ok?: boolean }>('/settings/opteemum/cmd/commands', fields)
}

export function updateCmdCommand(fields: Record<string, PostValue>) {
  return apiPost<{ ok?: boolean }>('/settings/opteemum/cmd/commands/update', fields)
}

export function removeCmdCommand(id: number) {
  return apiPost<{ ok?: boolean }>(`/settings/opteemum/cmd/commands/${id}/remove`, {})
}

/** Subcategoriile unei categorii - lista a doua a formularului. */
export function cmdSubcategories(category: number) {
  return apiGet<{ category: number; subcategories: Array<Record<string, unknown>> }>(
    `/settings/opteemum/cmd/categories/${category}/subcategories`,
  )
}

export function agentCommandsQuery() {
  return queryOptions({
    queryKey: ['settings', 'opteemum', 'agent-commands'],
    queryFn: () => apiGet<{ get_groups: Array<Record<string, unknown>> }>('/settings/opteemum/agent-commands'),
  })
}

export function agentCommandsTableQuery() {
  return queryOptions({
    queryKey: ['settings', 'opteemum', 'agent-commands-table'],
    queryFn: () => apiGet<{ data: Array<Record<string, unknown>> }>('/settings/opteemum/agent-commands/table?start=0&length=200'),
  })
}

export function editAgentCommand(sub: number) {
  return apiGet<{ get_command: Record<string, unknown>; get_groups: Array<Record<string, unknown>> }>(
    `/settings/opteemum/agent-commands/${sub}`,
  )
}

/**
 * Amandoua raspund cu `status` - 'OK' sau 'ERROR' - si cu un `message` care
 * spune ce camp lipseste. Cine nu se uita la el arata „salvat" pentru o
 * salvare care n-a avut loc.
 */
export function saveEditAgentCommand(sub: number, fields: Record<string, PostValue>) {
  return apiPost<{ status?: string; message?: string }>(`/settings/opteemum/agent-commands/${sub}`, fields)
}

export function insertAgentCommand(fields: Record<string, PostValue>) {
  return apiPost<{ status?: string; message?: string }>('/settings/opteemum/agent-commands', fields)
}

/**
 * Raspunsul unei liste paginate pe server, in forma pe care o stie DataTables.
 * Capetele din `SettingsOpteemumController` intorc de mult `draw`,
 * `recordsTotal`, `recordsFiltered` si `data` - numai clientul cerea o pagina
 * fixa de 200 si citea doar `data`.
 */
export type OpteemumTable = {
  draw: number
  recordsTotal: number
  recordsFiltered: number
  data: Array<Record<string, unknown>>
}

/** O pagina din lista de comenzi MACOS: cautarea si pagina pleaca la server. */
export function macosCommandsTableQuery(r: { start: number; length: number; search: string }) {
  const params = new URLSearchParams({ start: String(r.start), length: String(r.length), search: r.search })

  return queryOptions({
    queryKey: ['settings', 'opteemum', 'macos-table', r],
    queryFn: () => apiGet<OpteemumTable>('/settings/opteemum/macos-commands/table?' + params.toString()),
  })
}

export function insertMacosCommand(fields: Record<string, PostValue>) {
  return apiPost<{ status?: string }>('/settings/opteemum/macos-commands', fields)
}

export function standardCommandsQuery() {
  return queryOptions({
    queryKey: ['settings', 'opteemum', 'standard'],
    queryFn: () =>
      apiGet<{ categories: Array<Record<string, unknown>>; commandTypes: Array<Record<string, unknown>> }>(
        '/settings/opteemum/standard-commands',
      ),
  })
}

export function createStandardCommand(fields: Record<string, PostValue>) {
  return apiPost<{ ok?: boolean }>('/settings/opteemum/standard-commands', fields)
}

/** Actualizarea poarta `ID`, ca `updateCommandForm` din macheta veche. */
export function updateStandardCommand(fields: Record<string, PostValue>) {
  return apiPost<{ ok?: boolean }>('/settings/opteemum/standard-commands/update', fields)
}

export function removeStandardCommand(id: number) {
  return apiPost<{ ok?: boolean }>(`/settings/opteemum/standard-commands/${id}/remove`, {})
}

export function standardCommandsTableQuery() {
  return queryOptions({
    queryKey: ['settings', 'opteemum', 'standard-table'],
    queryFn: () => apiGet<{ data: Array<Record<string, unknown>> }>('/settings/opteemum/standard-commands/table?start=0&length=200'),
  })
}

/** Un capitol al usii: eticheta lui si semnalele care cad in el. */
export type AlarmChapter = { label: string; types: Array<Record<string, unknown>> }

/** O banda: de la cat, pana la cat, cu ce gravitate, si daca e din fabrica. */
export type AlarmBand = {
  flag: number
  from: string
  to: string
  on: boolean
  dinamic: number
  factory: boolean
}

export type AlarmSignal = {
  id: number
  name: string
  hint: string
  chapter: string
  dinamic: number
  sign: string
  watched: boolean
  loaded: boolean
  bands: AlarmBand[]
}

export type HardwareAlarmsPage = {
  title: string
  blurb: string
  door: string
  chapters: Record<string, AlarmChapter>
  groups: Array<Record<string, unknown>>
  canSp: boolean
  canCompany: boolean
  canGroups: boolean
}

export function hardwareAlarmsQuery() {
  return queryOptions({
    queryKey: ['settings', 'opteemum', 'hardware-alarms'],
    queryFn: () => apiGet<HardwareAlarmsPage>('/settings/opteemum/hardware-alarms'),
  })
}

/**
 * Semnalele usii pe stratul ales. Stratul si grupul intra in cheie, deci
 * schimbarea lor aduce lista din nou, fara sa se amestece cu cea dinainte.
 */
export function alarmMatrixQuery(layer: string, group: number) {
  return queryOptions({
    queryKey: ['settings', 'opteemum', 'hardware-alarms', 'matrix', layer, group],
    queryFn: () =>
      apiGet<{ layer: string; group: number; signals: AlarmSignal[] }>(
        `/settings/opteemum/hardware-alarms/matrix?layer=${encodeURIComponent(layer)}&group=${group}`,
      ),
  })
}

export function addKit(form: FormData) {
  return apiPostForm<{ ok?: boolean; message?: string; kitId?: number }>('/settings/opteemum/kits', form)
}

export function addCustomKit(form: FormData) {
  return apiPostForm<{ ok?: boolean; message?: string; kitId?: number }>('/settings/opteemum/custom-kits', form)
}

export function addCustomPackage(fields: Record<string, PostValue>) {
  return apiPost<{ ok?: boolean; message?: string }>('/settings/opteemum/packages', fields)
}
